import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { saveAs } from "file-saver";
import numeral from "numeral";
import dayjs from "dayjs";
import { message } from "antd";

type DownloadPaymentsParams = {
  invoiceNumber?: string;
  patientFirstName?: string;
  patientLastName?: string;
  dateOfBirth?: string;
  npi?: string;
  total?: number;
};

const formatPaymentsForCSV = (payments: any[]) => {
  const headers = [
    'Invoice Number',
    'Benefit Type',
    'First Name',
    'Last Name',
    'Date of Birth',
    'Date of Service',
    'Amount Paid',
    'Business Name',
    'Program Name',
    'Mailing Address',
    'Line2',
    'City',
    'State',
    'Zip'
  ];

  const rows = payments.map(payment => [
    payment.invoice_number || 'N/A',
    payment.payable_type === 'AssistanceRequest' ? 'Medical' : 'Pharmacy',
    payment.patient_first_name,
    payment.patient_last_name,
    payment.date_of_birth ? dayjs(payment.date_of_birth).format('MM-DD-YYYY') : 'N/A',
    payment.date_of_service ? dayjs(payment.date_of_service).format('MM-DD-YYYY') : 'N/A',
    numeral(payment.amount_paid).format('$0,0.00'),
    payment.business_name,
    payment.program_name,
    payment.reimbursement_address || payment.business_address,
    payment.reimbursement_address_line2 || payment.business_address_line_2,
    payment.reimbursement_address_city || payment.business_city,
    payment.reimbursement_address_state || payment.business_state,
    payment.reimbursement_address_zip || payment.business_zip
  ]);

  return [headers, ...rows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');
};

export const useDownloadPayments = () => {
  return useMutation({
    mutationFn: async (params: DownloadPaymentsParams) => {
      const searchParams = new URLSearchParams();
      
      if (params.invoiceNumber) {
        searchParams.append('invoice_number', params.invoiceNumber);
      }
      if (params.patientFirstName) {
        searchParams.append('patient_first_name', params.patientFirstName);
      }
      if (params.patientLastName) {
        searchParams.append('patient_last_name', params.patientLastName);
      }
      if (params.dateOfBirth) {
        searchParams.append('date_of_birth', params.dateOfBirth);
      }
      if (params.npi) {
        searchParams.append('npi', params.npi);
      }
      if (params.total) {
        searchParams.append('per', params.total.toString());
      }

      try {
        const { data } = await axios.get(`payments?${searchParams.toString()}`);
        const csvContent = formatPaymentsForCSV(data);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `payments_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`);
      } catch (error) {
        message.error('Failed to download payments, please try again later');
      }
    }
  });
};
